import axios, { AxiosError, type AxiosInstance } from 'axios'

import { ApiError } from '@/app/api/errors'
import type { ApiErrorBody } from '@/types/api'

/**
 * The single HTTP client for the versioned REST API.
 *
 * Authentication is a same-origin HttpOnly session cookie: no bearer token is ever
 * held in JavaScript or browser storage (SDD §7.2, ADR-004,
 * ARCHITECTURE_GUARDRAILS.md §14.6).
 */

const CSRF_COOKIE = 'csrftoken'
const CSRF_HEADER = 'X-CSRFToken'
const REQUEST_ID_HEADER = 'X-Request-ID'
const TENANT_HEADER = 'X-Tenant-ID'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/**
 * Resolves the logical tenant UUID for the active context.
 *
 * This is an *identifier*, not a database selector. The server validates membership
 * and resolves routing itself; the browser never sees or sends a database alias,
 * host or credential (ARCHITECTURE_GUARDRAILS.md §4.2).
 *
 * Wired to the session store when tenant selection lands (backlog P1).
 */
let resolveTenantId: () => string | null = () => null

export function setTenantResolver(resolver: () => string | null): void {
  resolveTenantId = resolver
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
})

api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toUpperCase()
  if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
    const token = readCookie(CSRF_COOKIE)
    if (token) config.headers.set(CSRF_HEADER, token)
  }

  const tenantId = resolveTenantId()
  if (tenantId) config.headers.set(TENANT_HEADER, tenantId)

  if (!config.headers.has(REQUEST_ID_HEADER)) {
    config.headers.set(REQUEST_ID_HEADER, crypto.randomUUID())
  }
  return config
})

export const CHECKIN_REQUIRED = 'attendance_checkin_required'

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    if (error.response) {
      // BRD-077: the server refuses the workspace until this person checks in; the
      // router guard listens and sends them to check-in
      if ((error.response.data as { code?: string } | undefined)?.code === CHECKIN_REQUIRED) {
        window.dispatchEvent(new CustomEvent(CHECKIN_REQUIRED))
      }
      throw new ApiError(
        error.response.status,
        error.response.data ?? {},
        'The request could not be completed.',
      )
    }
    // No response at all: offline, DNS failure or timeout.
    throw new ApiError(0, { code: 'network_unavailable' }, 'The server could not be reached.')
  },
)
